/**
 * Who sees the archive action, and how an archived user is described.
 *
 * Mirrors src/modules/users/archivePolicy.ts on the backend. The server is the
 * authority — this exists so the action is absent for people who cannot use it,
 * rather than present and answering 403, which is what the old delete button
 * did to every builder.
 */

const MANAGEMENT = 'Management';
const HIGH = 'High';

function roleName(user) {
  return String(user?.role?.name ?? user?.role_name ?? '').toLowerCase();
}

function verticalOf(user) {
  return String(user?.vertical ?? user?.designation?.vertical ?? '');
}

/** The account owner, or a Management Admin. */
export function canArchiveUsers(user) {
  if (!user) return false;
  if (roleName(user) === 'builder') return true;
  return verticalOf(user) === MANAGEMENT && user?.level === HIGH;
}

/** Purge destroys personal data — Management Admin only, not even the builder. */
export function canPurgeUsers(user) {
  if (!user) return false;
  return verticalOf(user) === MANAGEMENT && user?.level === HIGH;
}

export function isArchived(user) {
  return Boolean(user?.is_archived || user?.deleted_at);
}

/**
 * The one place a user's name is turned into display text, so the archived
 * treatment is consistent instead of being reinvented per screen.
 */
export function formatUserName(user, { suffix = true } = {}) {
  const name = user?.name || user?.email || 'Unknown user';
  if (suffix && isArchived(user)) return `${name} (archived)`;
  return name;
}

/**
 * Option lists split in two, and the difference is behavioural:
 *
 *  - Assignment pickers ("assign to", "successor", "approver") must offer live
 *    users only. An archived user must never become newly responsible for
 *    anything.
 *  - Filter pickers ("show leads for", report group-by) keep any archived user
 *    the current filter already references, so the Select finds its value.
 */
export function forAssignment(users = []) {
  return users.filter((u) => !isArchived(u));
}

export function forFiltering(users = [], referencedIds = []) {
  const keep = new Set(
    (Array.isArray(referencedIds) ? referencedIds : [referencedIds]).map(Number)
  );
  return users.filter((u) => !isArchived(u) || keep.has(Number(u?.id)));
}

/** Projects a user is mapped to, from whichever shape the API returned. */
export function userProjectIds(user) {
  const maps = user?.userProjectMapping ?? [];
  return maps
    .map((m) => Number(m?.project_id ?? m?.project?.id))
    .filter((n) => Number.isFinite(n) && n > 0);
}

/**
 * Who may inherit a given category of work.
 *
 * The preflight tells us which projects the items actually belong to. Someone
 * sharing none of them would inherit a list they cannot see, so they are not
 * offered. Management spans projects and is always eligible.
 *
 * This mirrors successorInScope on the server, which re-checks it — a filtered
 * dropdown is a convenience, never a control.
 */
export function candidatesForScope(users = [], scope, excludeId) {
  const projectIds = (scope?.projectIds ?? []).map(Number);
  return forAssignment(users).filter((u) => {
    if (excludeId != null && Number(u.id) === Number(excludeId)) return false;
    if (!projectIds.length) return true;
    if (String(u?.vertical ?? '') === 'Management') return true;
    return userProjectIds(u).some((id) => projectIds.includes(id));
  });
}

/** Human label for a handover category, used when the server sends no label. */
export const BLOCKER_LABELS = {
  open_leads: 'Open leads',
  open_pre_leads: 'Pre-leads',
  open_follow_ups: 'Scheduled follow-ups',
  open_tasks: 'Open tasks',
  open_sub_tasks: 'Open sub-tasks',
  sub_task_approvals: 'Sub-task approvals',
  open_change_requests: 'Change requests',
  project_approver: 'Project approver slots',
  direct_reports: 'Direct reports'
};

/** React Query keys to invalidate after an archive, so nothing stale lingers. */
export const ARCHIVE_INVALIDATION_KEYS = [
  ['users'],
  ['leads'],
  ['salesUsers'],
  ['allUsers'],
  ['userTrash']
];
